import { type ReactNode } from 'react'
import cx from 'classnames'
import styles from './DocumentsList.module.css'
import {
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  type TableSortOrder,
} from '@/components/Table'
import { FileTypeIcon } from '@/components/FileTypeIcon'
import { getFileIconType } from '@/utils/icon-util'
import { type AvatarSubject, Avatar } from '@/components/Avatar'
import { RadialProgress } from '@/components/RadialProgress'
import { Icon } from '@/components/Icon'
import { RawButton } from '@/components/Button'
import { Spinner } from '@/components/Spinner'
import { type DocumentRejectionStatus } from '@/features/documents/DocumentViewModel'
import { Dropdown, DropdownItem, DropdownTitle } from '@/components/Dropdown'
import { IconButton } from '@/components/IconButton'
import { DocumentListHeader } from './DocumentListHeader'
import { Pill } from '@/components/Pill'
import { dateFormatters } from '@/utils/date-util'

export interface DocumentsListFrameProps {
  children: ReactNode
  header: ReactNode
}

export function DocumentsListFrame({
  children,
  header,
}: DocumentsListFrameProps) {
  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <div className={styles.headerContainer}>{header}</div>
        <div className={styles.box}>{children}</div>
      </div>
    </div>
  )
}

export interface DocumentsListProps {
  searchText: string
  onSearchTextChange: (text: string) => unknown
  onQueueFilesUpload: (files: FileList) => void
  canUploadFiles: boolean
  table: ReactNode
  scrollLoader: ReactNode
}

export function DocumentsList({
  searchText,
  onSearchTextChange,
  onQueueFilesUpload,
  canUploadFiles,
  table,
  scrollLoader,
}: DocumentsListProps) {
  return (
    <>
      <div className={styles.actions}>
        <DocumentListHeader
          showUploadAction={canUploadFiles}
          searchText={searchText}
          onSearchTextChange={onSearchTextChange}
          onQueueFilesUpload={onQueueFilesUpload}
        />
      </div>
      <div className={styles.table}>
        {table}
        {scrollLoader}
      </div>
    </>
  )
}

export interface DocumentsTableColumn {
  key: string
  header: string
  sortable?: boolean
  sortOrder: TableSortOrder
}

export interface DocumentsTableProps {
  columns: DocumentsTableColumn[]
  onSort: (key: string, sortOrder: TableSortOrder) => unknown
  children?: ReactNode
}

export function DocumentsTable({
  columns,
  onSort,
  children,
}: DocumentsTableProps) {
  return (
    <Table striped stickyHeader>
      <TableHead>
        {columns.map((col) => (
          <TableHeader
            key={col.key}
            canSort={col.sortable}
            sortOrder={col.sortOrder}
            onSort={(newSort) => onSort(col.key, newSort)}
            align={'left'}
          >
            {col.header}
          </TableHeader>
        ))}
        <TableHeader></TableHeader>
      </TableHead>
      <TableBody>{children}</TableBody>
    </Table>
  )
}

export interface DocumentsTableRowProps {
  title: string
  archived: boolean
  isPurged: boolean
  author: AvatarSubject
  /**
   * `undefined` hides the workspace column.
   */
  workspaceName?: string
  documentTypeName?: string
  fileType: string
  dateModified: Date
  /**
   * Upload progress percentage while the document is still uploading.
   */
  uploadPercent: number | null
  isProcessing: boolean
  rejection: DocumentRejectionStatus | null
  downloadable: boolean
  onView: () => void
  onDownload: () => void
  onDelete: () => void
}

export function DocumentsTableRow({
  title,
  archived,
  isPurged,
  author,
  workspaceName,
  documentTypeName,
  fileType,
  dateModified,
  uploadPercent,
  isProcessing,
  rejection,
  downloadable,
  onView,
  onDownload,
  onDelete,
}: DocumentsTableRowProps) {
  return (
    <TableRow>
      <TableCell>
        <DocumentItem
          title={title}
          archived={archived}
          isPurged={isPurged}
          author={author}
          onView={onView}
        />
      </TableCell>
      {workspaceName !== undefined && (
        <TableCell width={200}>{workspaceName}</TableCell>
      )}
      <TableCell width={180}>{documentTypeName}</TableCell>
      <TableCell width={125} className={styles.muted}>
        {fileType}
      </TableCell>
      <TableCell width={220}>
        {dateFormatters.dateTimeDefault.format(dateModified)}
      </TableCell>
      <TableCell align="right" width={20}>
        <ItemIcon
          title={title}
          uploadPercent={uploadPercent}
          isProcessing={isProcessing}
          rejection={rejection}
          downloadable={downloadable}
          onDownload={onDownload}
          onView={onView}
          onDelete={onDelete}
        />
      </TableCell>
    </TableRow>
  )
}

interface DocumentItemProps {
  title: string
  archived: boolean
  isPurged: boolean
  author: AvatarSubject
  onView?: () => void
}

function DocumentItem({
  title,
  archived,
  isPurged,
  author,
  onView = () => undefined,
}: DocumentItemProps) {
  const icon = getFileIconType(title || '')

  return (
    <div
      className={styles.documentItem}
      data-test={'document-list-item-' + title}
    >
      <div className={styles.icon}>
        <FileTypeIcon
          height={36}
          primaryColor={icon.color}
          strokeColor="#bcbcbe"
          type={icon.type}
        />
      </div>
      <div className={styles.info}>
        {archived && (
          <div>
            <Pill color="red" shape="square" trim>
              Archived
            </Pill>
          </div>
        )}
        <div className={styles.title}>
          <RawButton className={styles.titleLink} onPress={() => onView()}>
            {title}
          </RawButton>
          {isPurged && <span className={styles.purged}>Purged</span>}
        </div>
        <div className={styles.meta}>
          <div className={styles.metaItem}>
            <Avatar size={20} subject={author} />
          </div>
        </div>
      </div>
    </div>
  )
}

interface ItemIconProps {
  title: string
  uploadPercent: number | null
  isProcessing: boolean
  rejection: DocumentRejectionStatus | null
  downloadable: boolean
  onDownload: () => void
  onView: () => void
  onDelete: () => void
}

function ItemIcon({
  title,
  uploadPercent,
  isProcessing,
  rejection,
  downloadable: isDownloadable,
  onDownload,
  onView,
  onDelete,
}: ItemIconProps) {
  if (uploadPercent !== null) {
    return <RadialProgress size={24} progress={uploadPercent} />
  }

  if (isProcessing) {
    return (
      <span
        className={styles.status}
        title="This document is currently being processed"
      >
        <Spinner />
      </span>
    )
  }

  if (rejection) {
    return rejection === 'Quarantined' ? (
      <span
        className={cx(styles.status, styles.virus)}
        title="This document is infected with a virus and is not available for download"
      >
        <Icon name="virus" />
      </span>
    ) : (
      <span
        className={cx(styles.status, styles.failed)}
        title="This document has failed to upload"
      >
        <Icon name="info" />
      </span>
    )
  }

  return (
    <span className={styles.status}>
      <Dropdown
        autoDismiss
        position="bottom-end"
        button={
          <IconButton
            name="more"
            label="Document actions"
            testId={'download-actions-dropdown-' + title}
          />
        }
      >
        <DropdownTitle>Actions</DropdownTitle>
        {isDownloadable && (
          <>
            <DropdownItem
              icon={<Icon name="arrow"></Icon>}
              onPress={() => onView()}
            >
              View Details
            </DropdownItem>
            <DropdownItem
              icon={<Icon name="download"></Icon>}
              onPress={() => onDownload()}
            >
              Download
            </DropdownItem>
          </>
        )}
        <DropdownItem
          icon={<Icon name="delete"></Icon>}
          onPress={() => onDelete()}
        >
          Delete
        </DropdownItem>
      </Dropdown>
    </span>
  )
}
